import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, resource } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsFormFieldModule,
  DsMessageModule,
  DsSegmentedControlModule,
  DsSelectModule,
  DsTooltipDirectiveModule,
  SelectListOption,
} from '@bmw-ds/components';

import { AuthenticationService } from '../../auth/authentication/authentication.service';
import { DealerProvider } from '../../auth/dealer/dealer.provider';
import { UserService } from '../../auth/user/user.service';
import { I18nService, useI18n } from '../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../loading/loading-indicator.component';
import { TopSectionComponent } from '../navigation/components/top-section/top-section.component';
import { FeatureFlagClient } from './client/feature-flag.client';
import { FeatureFlagViewModel } from './client/model/feature-flag.model';
import { TranslationsComponent } from './components/translations/translations.component';
import { FeatureFlagMapper } from './mapper/feature-flag.mapper';

@Component({
  selector: 'cdf-feature-flag',
  providers: [],
  imports: [
    DsFormFieldModule,
    DsSelectModule,
    DsSegmentedControlModule,
    DsButtonModule,
    DsTooltipDirectiveModule,
    FormsModule,
    DsMessageModule,
    TopSectionComponent,
    LoadingIndicatorComponent,
    TranslationsComponent,
    DsBoxModule,
  ],
  template: `
    <cdf-top-section [disabled]="!hasUnsavedChanges()">
      <h2 id="feature-flag-header">{{ i18n('adminMode.featureFlag') }}</h2>

      @if (selectedCountry(); as selectedCountryValue) {
        @if (viewModel(); as viewModel) {
          <ds-form-field class="mt-4x">
            <label ds-label>Country</label>
            <ds-select
              id="country-selection"
              [(ngModel)]="selectedCountry"
              [isFilterable]="true"
              [options]="countryTree()"
              [disabled]="!hasUnsavedChanges()"
            />
          </ds-form-field>

          <div class="ds-container-fluid mt-6x" id="feature-configuration">
            <div class="row header-row">
              <div class="col-6">Feature</div>
              <div class="col-6">Configuration</div>
            </div>

            @for (settings of viewModel[selectedCountryValue]; track settings.feature) {
              @let prevItem = viewModel[selectedCountryValue][$index - 1];
              @if (prevItem === undefined || prevItem.group.id !== settings.group.id) {
                <div class="row">
                  <div class="col-12">
                    <div class="mt-2x p-2x bg-neutral">
                      <span class="title-sm">{{ settings.group.displayText }}</span
                      ><br />
                      <span class="text-smallprint">{{ settings.group.description }}</span>
                    </div>
                  </div>
                </div>
              }
              <div
                class="row feature-row"
                [class.model-updated]="settings.unchangedModel !== settings.model()"
                [attr.data-group-id]="settings.group.id"
              >
                <div class="col-6">
                  <span class="ms-4x">{{ settings.text.label }}</span>
                  <ds-button
                    class="information"
                    icon="information"
                    size="sm"
                    variant="ghost"
                    [ds-tooltip]="settings.text.description"
                    [dsTooltipConfig]="{ origin: 'bottom', maxWidth: '25ch' }"
                  />
                </div>
                <div class="col-6">
                  <ds-select
                    [(ngModel)]="settings.model"
                    [options]="settings.options"
                    [readonly]="selectedCountryIsReadOnly()"
                  />
                </div>
              </div>
            }
          </div>

          <div class="button-container mt-6x">
            <button ds-button [disabled]="hasUnsavedChanges()" (click)="reset()" variant="outline">Reset</button>
            <button ds-button [disabled]="hasUnsavedChanges()" (click)="update()" class="ms-2x">Save</button>
          </div>
          <ds-hint tone="info" class="mt-2x ms-auto"
            >The updates will be applied after a delay up to one minute for users the next time they load the page.
          </ds-hint>
        } @else if (detailedCountryConfigsResource.isLoading()) {
          <cdf-loading-indicator>Loading...</cdf-loading-indicator>
        } @else {
          <ds-message
            tone="critical"
            [isDismissable]="false"
            messageText="An error has occurred, the configuration could not be loaded. Please create a support ticket."
          />
        }
      }
      <cdf-translations [languages]="supportedLanguages()" />
    </cdf-top-section>
  `,
  styleUrl: './feature-flag.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureFlagPage {
  protected readonly i18n = useI18n();
  private readonly featureFlagClient = inject(FeatureFlagClient);
  private readonly featureFlagMapper = inject(FeatureFlagMapper);
  private readonly dealerProvider = inject(DealerProvider);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthenticationService);

  readonly selectedCountry = linkedSignal<string | undefined>(() => {
    return this.dealerProvider.activeCountry();
  });

  readonly viewModel = linkedSignal<FeatureFlagViewModel | undefined>(() => {
    const configs = this.detailedCountryConfigsResource.value();
    if (!configs) {
      return undefined;
    }
    return this.featureFlagMapper.mapToViewModel(configs);
  });

  protected readonly detailedCountryConfigsResource = resource({
    params: () => this.authService.authHeadersWithoutAdminDpNr(),
    loader: () => this.featureFlagClient.getDetailedCountryConfigs(),
  });

  readonly updatableCountries = computed(() => {
    return Array.from(this.userService.legalEntitiesForAdminMode().keys());
  });

  readonly countryTree = computed(() => {
    const featureFlagCountries = Object.keys(this.viewModel() ?? []);
    const updatableCountries = this.updatableCountries();
    const readOnlyCountries = featureFlagCountries.filter((x) => !updatableCountries.includes(x));

    const tree: SelectListOption[] = [
      {
        id: 'updatable',
        label: 'Updatable',
        group: true,
        children: updatableCountries.map((country) => ({
          id: country,
          label: country,
        })),
      },
      {
        id: 'read-only',
        label: 'Read-Only',
        group: true,
        children: readOnlyCountries.map((country) => ({
          id: country,
          label: country,
        })),
      },
    ];

    return tree;
  });

  readonly selectedCountryIsReadOnly = computed(() => {
    return !this.updatableCountries().includes(this.selectedCountry() ?? '');
  });

  readonly viewModelAsUpdates = computed(() => {
    const viewModel = this.viewModel();
    const country = this.selectedCountry();
    if (!viewModel || !country) {
      return [];
    }
    const viewModelElements = viewModel[country];
    if (!viewModelElements) {
      return [];
    }
    return this.featureFlagMapper.mapFromViewModel(viewModelElements);
  });

  protected reset() {
    this.viewModel.update((current) => {
      if (!current) {
        return undefined;
      }
      for (const config of Object.values(current)) {
        for (const element of config) {
          element.model.set(element.unchangedModel);
        }
      }
      return current;
    });
  }

  readonly hasUnsavedChanges = computed(() => this.viewModelAsUpdates().length === 0);

  protected async update() {
    const updates = this.viewModelAsUpdates();
    const country = this.selectedCountry();
    if (country) {
      const updateResponse = await this.featureFlagClient.updateCountryConfigs(country, updates);

      const partialViewModel = this.featureFlagMapper.mapToViewModel(updateResponse);
      this.viewModel.set({
        ...(this.viewModel() ?? {}),
        [country]: partialViewModel[country],
      });

      const updatedConfig = {
        ...(this.userService.countryConfiguration() ?? {}),
        ...updateResponse,
      };
      this.userService.updateCountryConfiguration(updatedConfig);
    }
  }

  protected supportedLanguages() {
    return I18nService.supportedLocales.map((locale) => locale.id);
  }
}
